import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InteractiveElement, borderRadius, iconButtonSize, styled, media } from "@iventis/styles";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { TimeFilterComponent, DateFilterComponent, SimpleTooltip, DisabledOverlay } from "@iventis/components";
import { parseEpochSecondsToDate, parseTimeNumberToDate } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { checkModeIsPresent } from "@iventis/map-engine/src/machines/mode-machine.helpers";
import { RootDispatch, RootState } from "./state/root.store";
import { map } from "./state/map.slice";
import { useDateFilterUrlState } from "./hooks/use-date-filter-url-state";

const PlanDateFilter = () => {
    const dispatch: RootDispatch = useDispatch();
    const translate = useIventisTranslate();

    const { filter, time, day, mode } = useSelector(
        (state: RootState) => ({
            filter: state.mapReducer.mapModule.datesFilter.value.filter,
            day: state.mapReducer.mapModule.datesFilter.value.day,
            time: state.mapReducer.mapModule.datesFilter.value.time,
            stamp: state.mapReducer.mapModule.datesFilter.stamp,
            mode: state.mapReducer.mapModule.mode.value,
        }),
        (prev, next) => prev.stamp === next.stamp && prev.mode === next.mode
    );

    const { updateDayParam, updateTimeParam, updateFilter } = useDateFilterUrlState();

    const onDateFilterToggle = () => {
        const updatedFilterValue = !filter;
        updateFilter(updatedFilterValue);
        dispatch(map.actions.toggleDateFilter(updatedFilterValue));
        if (updatedFilterValue) {
            incrementMonitoringCount({ name: MetricName.DATE_FILTER_USED });
        }
    };

    const onDayChange = (date: Date) => {
        dispatch(map.actions.setDateFilterDay(date));
        updateDayParam(date);
    };

    const onTimeChange = (time: Date) => {
        dispatch(map.actions.setDateFilterTime(time));
        updateTimeParam(time);
    };

    const dayDate = parseEpochSecondsToDate(day);
    const timeDate = parseTimeNumberToDate(time);
    const isInCompositionMode = checkModeIsPresent(mode, "composition");

    return (
        <PlanDateFilterContainer>
            {filter && (
                <>
                    <TimeFilterComponent time={timeDate} incrementAmount={15} onTimeChange={onTimeChange} />
                    <DateFilterComponent date={dayDate} onDateChange={onDayChange} />
                </>
            )}
            <SimpleTooltip text={translate(Content.map8.dates.calendarTooltip)} placement="top">
                <div>
                    {isInCompositionMode && <DisabledOverlay />}
                    <InteractiveElement data-testid="map-date-filter-button" data-filter={filter} className="calendar-button" onClick={onDateFilterToggle}>
                        <FontAwesomeIcon icon={["far", filter ? "xmark" : "calendar-clock"]} size="lg" />
                    </InteractiveElement>
                </div>
            </SimpleTooltip>
        </PlanDateFilterContainer>
    );
};

export const PlanDateFilterComponent = React.memo(PlanDateFilter);

const PlanDateFilterContainer = styled.div`
    position: absolute;
    bottom: 35px;
    right: 10px;
    flex-direction: column;
    ${media.medium} {
        bottom: 40px;
        right: 60px;
        flex-direction: row;
        height: 50px;
    }

    display: flex;
    align-items: flex-end;
    gap: 10px;

    .calendar-button {
        background-color: ${(props) => props.theme.typographyColors.blank};
        height: ${iconButtonSize};
        width: ${iconButtonSize};
        padding: 5px;
        border-radius: ${borderRadius.standard};
        box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.25);

        :disabled {
            cursor: default;
        }
    }
`;
